/**
 * RSI AND MACD IN THE OHLCV TOOLTIP.
 *
 * <p>Its own module for the reason `priceFormat` is: importing `EChartComponent` into a test pulls
 * echarts' ESM entry through jest, which does not transform it, and the suite fails to parse
 * before a single assertion runs. Everything here is string building over two arrays, which needs
 * no chart at all to be tested.
 */

/** A number worth printing. An indicator's early bars are null by construction, not by fault. */
const printable = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * The rows for whichever of RSI and MACD has a value at this bar.
 *
 * <h2>The blank line is a SPACER ROW, not a line break</h2>
 *
 * These rows join the OHLCV `<table>`, so the separation has to be a row — a `<br>` between two
 * `<tr>`s is not valid table content and browsers hoist it out of the table, which puts the gap
 * in the wrong place. A spacer row also keeps the two columns aligned with the prices above it,
 * which is the whole reason the tooltip is a table.
 *
 * <h2>It is empty when there is nothing to say, and that is the gating</h2>
 *
 * An indicator switched off has no series on the chart, so quoting it in the tooltip would be a
 * reading of something the reader cannot see. An indicator switched ON still has null values for
 * its first bars — RSI needs its period before it means anything — and printing `NaN` there would
 * look like a fault rather than like a warm-up.
 *
 * <p>Returning '' rather than a spacer alone matters: a blank line at the bottom of every tooltip
 * on a chart with no indicators is a gap with nothing under it.
 *
 * @param {number} index the bar's index
 * @param {object} sources
 * @param {Array<number|null>} [sources.rsi] the RSI series, aligned with the bars
 * @param {Array<number|null>} [sources.macd] the MACD line, aligned with the bars
 * @param {number} [sources.decimals] how many decimals MACD takes — it is a price difference and
 *                 on a sub-dollar instrument two decimals is every value reading 0.00
 * @returns {string} table rows, or '' when neither indicator has a value here
 */
export function indicatorRows(index, { rsi, macd, decimals = 2 } = {}) {
  const at = (series) => (Array.isArray(series) ? series[index] : undefined);
  const rsiValue = at(rsi);
  const macdValue = at(macd);
  const rows = [];
  // RSI is a 0..100 oscillator and is conventionally quoted to two decimals whatever the
  // instrument costs, so it does not take the price scale's precision.
  if (printable(rsiValue)) rows.push(['RSI', rsiValue.toFixed(2)]);
  if (printable(macdValue)) rows.push(['MACD', macdValue.toFixed(decimals)]);
  if (rows.length === 0) return '';
  const spacer = '<tr><td colspan="2" style="height:8px"></td></tr>';
  return spacer + rows.map(([label, value]) =>
    `<tr><td style="padding-right:8px">${label}:</td>`
    + `<td style="text-align:right">${value}</td></tr>`).join('');
}

/**
 * How many decimals a MACD reading takes, from the price it is a difference of.
 *
 * <p>MACD is the gap between two moving averages of PRICE, so it inherits the instrument's scale:
 * two decimals is right for an index at 771 and reads `0.00` for every bar of a pair at 1.0842.
 * The thresholds mirror {@link module:utils/priceFormat}, one step coarser — a difference needs
 * one fewer significant figure than the level it is taken from to say the same thing.
 */
export function macdDecimals(referencePrice) {
  if (!Number.isFinite(referencePrice)) return 2;
  const magnitude = Math.abs(referencePrice);
  if (magnitude >= 100) return 2;
  if (magnitude >= 1) return 3;
  return 5;
}
